import axios, { isAxiosError } from 'axios';

import { getApiHost } from '../utils/getApiHost';

/** Body of `GET /user/me` and `PATCH /user/me`. */
export interface UserProfile {
  userId: string;
  email: string;
  name: string;
}

type Listener = (profile: UserProfile | null, uid: string | null) => void;

let cached: UserProfile | null = null;
let cachedUid: string | null = null;
let inFlight: Promise<UserProfile> | null = null;
let inFlightUid: string | null = null;
/** Bumped when the cache is dropped so a late response cannot refill it. */
let generation = 0;

const listeners = new Set<Listener>();

const emit = () => {
  listeners.forEach((listener) => listener(cached, cachedUid));
};

export const subscribeUserProfile = (listener: Listener): (() => void) => {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
};

export const readCachedUserProfile = (
  uid: string | null | undefined
): UserProfile | null => {
  if (!uid || cachedUid !== uid) return null;
  return cached;
};

/** Drop the cache. Call on sign-out and when the signed-in uid changes. */
export const clearUserProfile = (): void => {
  cached = null;
  cachedUid = null;
  inFlight = null;
  inFlightUid = null;
  generation += 1;
  emit();
};

/** Replace the cache after a successful rename so every subscriber updates. */
export const setCachedUserProfile = (
  uid: string,
  profile: UserProfile
): void => {
  cached = profile;
  cachedUid = uid;
  emit();
};

/** Drop the cached profile but keep the uid, so the next read refetches. */
export const invalidateUserProfile = (): void => {
  cached = null;
  inFlight = null;
  inFlightUid = null;
  generation += 1;
  emit();
};

export const getUserProfile = (
  token: string,
  uid: string,
  { force = false }: { force?: boolean } = {}
): Promise<UserProfile> => {
  if (!force && cached && cachedUid === uid) return Promise.resolve(cached);
  if (!force && inFlight && inFlightUid === uid) return inFlight;

  const gen = ++generation;
  inFlightUid = uid;
  const request = axios
    .get<UserProfile>(`${getApiHost()}/user/me`, {
      headers: { Authorization: `Bearer ${token}` },
    })
    .then((res) => {
      if (gen !== generation) return res.data;
      cached = res.data;
      cachedUid = uid;
      emit();
      return res.data;
    })
    .finally(() => {
      if (gen === generation && inFlight === request) {
        inFlight = null;
        inFlightUid = null;
      }
    });
  inFlight = request;
  return request;
};

export const updateDisplayName = async (
  token: string,
  name: string
): Promise<UserProfile> => {
  const res = await axios.patch<UserProfile>(
    `${getApiHost()}/user/me`,
    { name },
    { headers: { Authorization: `Bearer ${token}` } }
  );
  return res.data;
};

/** Message for a failed rename. Pydantic 422 details are prefixed "Value error, ". */
export const profileErrorMessage = (err: unknown): string => {
  if (isAxiosError(err)) {
    const detail = (err.response?.data as { detail?: unknown } | undefined)
      ?.detail;
    if (typeof detail === 'string' && detail) return detail;
    if (Array.isArray(detail)) {
      const msg = (detail[0] as { msg?: unknown } | undefined)?.msg;
      if (typeof msg === 'string' && msg) {
        return msg.replace(/^Value error,\s*/i, '');
      }
    }
  }
  return "Couldn't save your username. Please try again.";
};
